import { IBotData } from "../shared/types";
import { hasReturnBasis } from "../utils/botMetrics";

export function combineBotsData(bots: IBotData[]): IBotData {
  if (bots.length === 0) throw new Error("No bots provided for combination");

  const combined: IBotData = {
    stats: {
      ...bots[0].stats,
      totalCapitalUSD: 0,
      pnlUSD: 0,
      totalPoolFeesUSDOrig: 0,
      botPremiums: 0,
      manualPremiums: 0,
      botPremiumsUSD: 0,
      depositsUSD: 0,
      withdrawalsUSD: 0,
      repurchaseCurValue: 0,
      currentPoolCollectedFees: 0,
      currentPoolAmount: 0,
      feesPerCapital: 0,
      pnlPerCapital: 0,
      hodlDiffUSD: 0,
      hodlValueUSD: undefined,
      accruedFeesUSD: 0,
      initialCapital: 0,
      balancingUSD: 0,
      lentUSD: 0,
      lendingPnl: 0,
      percentToken0: 0,
      percentToken1: 0,
      isLowGas: false,
      gasRequests: bots.some(bot => bot.stats.gasRequests !== undefined)
        ? bots.flatMap(bot => bot.stats.gasRequests || [])
        : undefined,
      // Reset snapshot accumulators to avoid mutation/double-counting from bots[0]
      balanceSnapshotEnd: {
        token0Balance: 0,
        token1Balance: 0,
        tokenNativeBalance: 0,
        token0Price: 0,
        token1Price: 0,
        totalValueInUSD: 0,
        token0Percentage: 0,
        token1Percentage: 0,
        walletToken0: 0,
        walletToken1: 0,
        walletValueUSD: 0,
        poolToken0: 0,
        poolToken1: 0,
        poolValueUSD: 0,
      }
    },
    positions: [],
    movements: [],
    
  };

  let repurchaseTotalAmount = 0;
  let repurchaseTotalCost = 0;
  let repurchaseTotalCurValue = 0;
  let returnPnl = 0;
  let activeBalancingStats: IBotData["stats"] | undefined;

  for (const bot of bots) {
    const s = bot.stats;
    combined.positions.push(...bot.positions);
    combined.movements.push(...bot.movements);

    combined.stats.totalCapitalUSD += s.totalCapitalUSD;
    combined.stats.pnlUSD += s.pnlUSD;
    combined.stats.totalPoolFeesUSDOrig += s.totalPoolFeesUSDOrig;
    combined.stats.botPremiums += s.botPremiums;
    combined.stats.manualPremiums += s.manualPremiums;
    combined.stats.botPremiumsUSD += s.botPremiumsUSD;
    combined.stats.currentPoolCollectedFees += s.currentPoolCollectedFees;
    combined.stats.currentPoolAmount += s.currentPoolAmount;
    // The family's capital basis (and so every % the card shows) covers only bots with a positive
    // basis: a bot that withdrew more than it put in has no meaningful return (backend: 0%).
    if (hasReturnBasis(s)) {
      combined.stats.depositsUSD += s.depositsUSD;
      combined.stats.withdrawalsUSD += s.withdrawalsUSD;
      combined.stats.initialCapital += s.initialCapital;
      returnPnl += s.pnlUSD;
    }

    // Safely aggregate balanceSnapshotEnd
    if (s.balanceSnapshotEnd) {
      combined.stats.balanceSnapshotEnd.totalValueInUSD += s.balanceSnapshotEnd.totalValueInUSD || 0;
      combined.stats.balanceSnapshotEnd.token0Balance += s.balanceSnapshotEnd.token0Balance || 0;
      combined.stats.balanceSnapshotEnd.token1Balance += s.balanceSnapshotEnd.token1Balance || 0;
    }

    combined.stats.balancingUSD += s.balancingUSD;
    combined.stats.lentUSD += s.lentUSD;
    combined.stats.accruedFeesUSD = (combined.stats.accruedFeesUSD || 0) + (s.accruedFeesUSD || 0);
    combined.stats.lendingPnl += s.lendingPnl;
    

    combined.stats.hodlDiffUSD += s.hodlDiffUSD ?? 0;
    if (s.isLowGas) combined.stats.isLowGas = true;
    if (!activeBalancingStats && s.currentBalancingStep) {
      activeBalancingStats = s;
    }

    // Per-bot repurchase figures come from the backend in USD (each bot counts only its own
    // buybacks), so the family values are plain sums.
    repurchaseTotalAmount += s.repurchaseAmount || 0;
    repurchaseTotalCost += (s.repurchaseAmount || 0) * (s.repurchaseAvgPrice || 0);
    repurchaseTotalCurValue += s.repurchaseCurValue || 0;
  }

  // HODL basket of the family: the sum of the bots' baskets, only when every bot has one.
  combined.stats.hodlValueUSD = bots.every((b) => Number.isFinite(b.stats.hodlValueUSD))
    ? bots.reduce((acc, b) => acc + (b.stats.hodlValueUSD as number), 0)
    : undefined;

  // Same basis as the backend (initial + deposits − withdrawals), over the bots that have one.
  // pnlUSD stays the sum of every bot (money made); the % uses only the P&L measured on that basis.
  const capitalBase = combined.stats.initialCapital + combined.stats.depositsUSD - combined.stats.withdrawalsUSD;
  combined.stats.pnlPerCapital = capitalBase > 0 ? returnPnl / capitalBase : 0;
  combined.stats.feesPerCapital = capitalBase > 0 ? combined.stats.totalPoolFeesUSDOrig / capitalBase : 0;

  // Token split of the family, weighted by each bot's on-chain value (percentages don't add up).
  const weight = bots.reduce((acc, b) => acc + (b.stats.balanceSnapshotEnd?.totalValueInUSD || 0), 0);
  if (weight > 0) {
    combined.stats.percentToken0 =
      bots.reduce((acc, b) => acc + (b.stats.percentToken0 || 0) * (b.stats.balanceSnapshotEnd?.totalValueInUSD || 0), 0) / weight;
    combined.stats.percentToken1 =
      bots.reduce((acc, b) => acc + (b.stats.percentToken1 || 0) * (b.stats.balanceSnapshotEnd?.totalValueInUSD || 0), 0) / weight;
  }

  combined.stats.repurchaseAmount = repurchaseTotalAmount;
  combined.stats.repurchaseAvgPrice = repurchaseTotalAmount > 0 ? repurchaseTotalCost / repurchaseTotalAmount : 0;
  combined.stats.repurchaseCurPrice = repurchaseTotalAmount > 0 ? repurchaseTotalCurValue / repurchaseTotalAmount : 0;
  combined.stats.repurchaseCurValue = repurchaseTotalCurValue;

  if (activeBalancingStats) {
    combined.stats.currentBalancingStep = activeBalancingStats.currentBalancingStep;
    combined.stats.currentBalancingSubStep = activeBalancingStats.currentBalancingSubStep;
    combined.stats.balancingClaimDate = activeBalancingStats.balancingClaimDate;
    combined.stats.balancingTokenAmount = activeBalancingStats.balancingTokenAmount;
    combined.stats.balancingSwapOriginalTarget = activeBalancingStats.balancingSwapOriginalTarget;
    combined.stats.balancingSwapRemaining = activeBalancingStats.balancingSwapRemaining;
    combined.stats.balancingSwapPurchased = activeBalancingStats.balancingSwapPurchased;
    combined.stats.balancingSwapTokenIn = activeBalancingStats.balancingSwapTokenIn;
    combined.stats.balancingSwapTokenOut = activeBalancingStats.balancingSwapTokenOut;
    combined.stats.balancingSwapProgressPct = activeBalancingStats.balancingSwapProgressPct;
    combined.stats.waitingFundsAmount = activeBalancingStats.waitingFundsAmount;
    combined.stats.waitingFundsToken = activeBalancingStats.waitingFundsToken;
    combined.stats.waitingFundsChainId = activeBalancingStats.waitingFundsChainId;
    combined.stats.waitingFundsAddress = activeBalancingStats.waitingFundsAddress;
    combined.stats.binanceDepositSubaccount = activeBalancingStats.binanceDepositSubaccount;
    combined.stats.binanceDepositToken = activeBalancingStats.binanceDepositToken;
    combined.stats.binanceDepositAmount = activeBalancingStats.binanceDepositAmount;
    combined.stats.telegramExpectedToken = activeBalancingStats.telegramExpectedToken;
    combined.stats.telegramExpectedAmount = activeBalancingStats.telegramExpectedAmount;
    combined.stats.telegramExpectedChainId = activeBalancingStats.telegramExpectedChainId;
    combined.stats.telegramDepositAddress = activeBalancingStats.telegramDepositAddress;
  }

  return combined;
}
